import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  Paper,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  FormControlLabel,
  Checkbox,
  alpha,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { httpRequest } from 'src/services/httpRequest';
import toast from 'react-hot-toast';
import { Edit, Warning } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux-store/appStore';

interface MemberPermission {
  UserID: number;
  FirstName: string;
  LastName: string;
  EmailID: string;
  InsertDate: string;
  Active: boolean;
  PhoneNumber: string;
  UserRole: string;
}

const MemberMaster: React.FC = () => {
  // State management

  const theme = useTheme();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [isActive, setIsActive] = useState(false); // State for "Active"
  const [userRole, setUserRole] = useState('Admin');

  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  const userRoles = userDetails.UserRole || '';

  // Admin data
  const [permissions, setPermissions] = useState<MemberPermission[]>([]);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // Edit mode state
  const [editMode, setEditMode] = useState(false);
  const [selectedUserID, setSelectedUserID] = useState<number | null>(null);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Form validation
  const validateForm = () => {
    if (!firstName.trim()) {
      setMessage('First name cannot be blank');
      return false;
    }
    if (!lastName.trim()) {
      setMessage('Last name cannot be blank');
      return false;
    }
    if (!email.trim()) {
      setMessage('Email cannot be blank');
      return false;
    }
    const emailRegex = /\s*\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*\s*/;
    if (!emailRegex.test(email)) {
      setMessage('Email is invalid');
      return false;
    }

    const phoneRegex = /^\d{3}-\d{3}-\d{4}$/;
    if (!phoneRegex.test(phoneNumber)) {
      setMessage('Phone number must be in the format XXX-XXX-XXXX');
      return false;
    }
    return true;
  };

  const formatPhoneNumber = (value: string) => {
    const input = value.replace(/\D/g, '').substring(0, 10);
    const first = input.substring(0, 3);
    const middle = input.substring(3, 6);
    const last = input.substring(6, 10);
    let finalValue = '';
    if (input?.length > 6) {
      finalValue = `${first}-${middle}-${last}`;
    } else if (input?.length > 3) {
      finalValue = `${first}-${middle}`;
    } else if (input?.length >= 0) {
      finalValue = input;
    }
    return finalValue;
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedPhone = formatPhoneNumber(e.target.value);
    setPhoneNumber(formattedPhone);
  };

  const fetchAdmins = async (pageNumber = 1, limit = rowsPerPage) => {
    setLoading(true);
    try {
      const fetchResponse = await httpRequest(
        'GET',
        `auth/admin?Page=${pageNumber}&Size=${limit}`
      );

      if (fetchResponse?.status === 'success') {
        setPermissions(fetchResponse.data.data || []);
        setTotalRecords(fetchResponse.data.totalRecords || 0);
        setPage(fetchResponse.data.currentPage - 1); // API is 1-based, UI is 0-based
      } else {
        toast.error('Failed to fetch admin data');
      }
    } catch (error) {
      toast.error(error?.message || 'Error fetching admin data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  // Handlers

  const clearForm = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhoneNumber('');
    setMessage('');
    setIsActive(false); // Reset Active field
    setEditMode(false);
    setSelectedUserID(null);
    fetchAdmins();
    setUserRole('Admin');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);

    const payload: any = {
      FirstName: firstName,
      LastName: lastName,
      EmailID: email,
      PhoneNumber: phoneNumber,
      UserRole: userRole
    };

    if (editMode) {
      payload.Active = isActive;
    }

    const endpoint = editMode ? `auth/admin/${selectedUserID}` : 'auth/admin';
    const method = editMode ? 'PATCH' : 'POST';

    try {
      const submitResponse = await httpRequest(method, endpoint, payload);

      if (submitResponse?.status === 'success') {
        toast.success(submitResponse?.message);
        clearForm();
      } else {
        toast.error(
          submitResponse?.message || 'Failed to fetch. Please try again later.'
        );
      }
    } catch (error) {
      toast.error(error?.message || 'Failed to fetch. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (member: MemberPermission) => {
    setFirstName(member.FirstName);
    setLastName(member.LastName);
    setEmail(member.EmailID);
    setPhoneNumber(member.PhoneNumber);
    setIsActive(member.Active); // Set Active status for editing
    setEditMode(true);
    setSelectedUserID(member.UserID);
    setUserRole(member.UserRole);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    fetchAdmins(newPage + 1, rowsPerPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newLimit = parseInt(event.target.value, 10);
    setRowsPerPage(newLimit);
    setPage(0);
    fetchAdmins(1, newLimit);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 'bold',
            color: theme.palette.primary.main,
            position: 'relative',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: 0,
              width: 110,
              height: 4,
              borderRadius: 2,
              backgroundColor: theme.palette.primary.main
            }
          }}
        >
          Manage Users
        </Typography>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={3}>
          {/* Form */}
          {userRoles === 'Admin' && (
            <Grid item xs={12}>
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      required
                      fullWidth
                      label="First Name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      required
                      fullWidth
                      label="Last Name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      required
                      fullWidth
                      label="Email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      required
                      fullWidth
                      label="Phone Number"
                      value={phoneNumber}
                      onChange={handlePhoneNumberChange}
                      placeholder="XXX-XXX-XXXX"
                      inputProps={{ maxLength: 12 }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Role</InputLabel>
                      <Select
                        required
                        value={userRole}
                        onChange={(e) => setUserRole(e.target.value)}
                        label="Role"
                        sx={{ borderRadius: 1 }}
                        disabled={editMode}
                      >
                        <MenuItem value="Admin"> Admin</MenuItem>
                        <MenuItem value="General Admin">General Admin</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {editMode && (
                    <Grid item xs={12} md={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isActive}
                            onChange={(e) => setIsActive(e.target.checked)}
                          />
                        }
                        label="Active"
                      />
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={loading}
                      >
                        {loading ? (
                          <CircularProgress size={24} />
                        ) : editMode ? (
                          'Update'
                        ) : (
                          'Submit'
                        )}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={clearForm}
                        disabled={loading}
                      >
                        Clear
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </form>
            </Grid>
          )}

          {/* Messages */}
          {message && (
            <Grid item xs={12}>
              <Alert severity="info">{message}</Alert>
            </Grid>
          )}

          {/* Conditional rendering of loader or table */}
          <Grid item xs={12}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Phone Number</TableCell>
                      <TableCell>Created At</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Active</TableCell>

                      {userRoles === 'Admin' && <TableCell>Actions</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {permissions && permissions?.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: 2
                            }}
                          >
                            <Warning
                              sx={{
                                fontSize: 40,
                                color: alpha(theme.palette.text.secondary, 0.6)
                              }}
                            />
                            <Typography
                              variant="body1"
                              color="text.secondary"
                              sx={{ fontWeight: 500 }}
                            >
                              No data found.
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      permissions &&
                      permissions?.length > 0 &&
                      permissions?.map((row) => (
                        <TableRow key={row?.UserID}>
                          <TableCell>{`${row?.FirstName} ${row?.LastName}`}</TableCell>
                          <TableCell>{row?.EmailID}</TableCell>
                          <TableCell>{row?.PhoneNumber}</TableCell>
                          <TableCell>{formatDate(row?.InsertDate)}</TableCell>
                          <TableCell>{row?.UserRole || '-'}</TableCell>
                          <TableCell>{row?.Active ? 'Yes' : 'No'}</TableCell>
                          {userRoles === 'Admin' && (
                            <TableCell>
                              <IconButton
                                onClick={() => handleEdit(row)}
                                size="small"
                                sx={{
                                  color: '#4285f4',
                                  backgroundColor: alpha('#4285f4', 0.05),
                                  '&:hover': {
                                    backgroundColor: alpha('#4285f4', 0.15)
                                  },
                                  transition: 'all 0.2s',
                                  padding: 1,
                                  border: `1px solid ${alpha('#4285f4', 0.2)}`
                                }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50]}
                  component="div"
                  count={totalRecords}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </TableContainer>
            )}
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default MemberMaster;
